import { Box, Grid, TextField, Typography } from "@mui/material";
import { LoginButton } from "components/Primitives";
import { EMAIL, PASSWORD, useLogin } from "hooks/useLogin";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { useLoginStyle } from "style/login.style";
import { API_ERROR } from "../../api/api-response";
import { IUser } from "../../domain/user/user";
import { IReturnUrl, LoginOperationResult, resolveReturnUrl } from "../../utils/auth";
import { AvailableBaseRoutes } from "../../utils/constants";
import config from "../../config/config";

export const LoginForm: React.FC = () => {
  const { t } = useTranslation();
  const { classes } = useLoginStyle();
  const [auth, setValue, handleLogin] = useLogin();
  const history = useHistory();
  const [error, setError] = useState<LoginOperationResult>();
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const location = useLocation();

  const login = async () => {
    setIsProcessing(true);
    const { result, user } = await handleLogin();

    if (result !== LoginOperationResult.Success) {
      setError(result);
      setIsProcessing(false);
      return;
    }

    handleRedirectForUser(user);
    setIsProcessing(false);
  };

  const handleRedirectForUser = (user: IUser | undefined) => {
    const state = location.state as IReturnUrl | null;

    if (!user) {
      // A 200 with no user means the login did not really complete, so hold on to
      // the pending return URL for the retry rather than consuming it.
      history.push(AvailableBaseRoutes.Login, { next: state?.next, nextOwner: state?.nextOwner });
      return;
    }

    // Router state wins over storage, storage is cleared either way, and a location
    // remembered for another login is dropped — see resolveReturnUrl.
    history.push(resolveReturnUrl(user.email, state) ?? AvailableBaseRoutes.Portal);
  };

  const getErrorMessageOfLoginOperationResult = (type: LoginOperationResult) => {
    switch (type) {
      case LoginOperationResult.WrongLoginData:
        return t("error.login.wrong_credentials");
      case LoginOperationResult.NoContracts:
        return t("error.login.no_active_contracts");
      case LoginOperationResult.TooManyRequest:
        return t("error.login.too_many_requests");
      default:
        return t("general.error_occurred", { errorCode: API_ERROR, errorMsg: "Unknown error while trying to login" });
    }
  };

  const onEnter = (ev: React.KeyboardEvent) => {
    if (ev.key === "Enter") {
      login();
    }
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <Typography color="primary" variant="h1">
          {t("general.login_title.text")}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Grid container>
          <Grid item xs={12}>
            <Box my={2}>
              <TextField
                value={auth.email}
                onChange={(e) => setValue(e.currentTarget.value, EMAIL)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                label={t("general.email.text")}
                id={EMAIL}
                sx={{ paddingTop: "20px" }}
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box my={2}>
              <TextField
                value={auth.password}
                fullWidth
                onChange={(e) => setValue(e.currentTarget.value, PASSWORD)}
                InputLabelProps={{ shrink: true }}
                label={t("general.password.text")}
                type="password"
                onKeyPress={onEnter}
                id={PASSWORD}
                sx={{ paddingTop: "20px" }}
              />
            </Box>
          </Grid>
          {!!error && (
            <Typography variant="body1" color="error" id="loginError" style={{ marginTop: 3 }}>
              {getErrorMessageOfLoginOperationResult(error)}
            </Typography>
          )}
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Box mt={3}>
          <Grid container direction="row-reverse" justifyContent="space-between" alignItems="center" spacing={2}>
            {config.bcForgotPasswordUrl && (
              <Grid item>
                <a
                  href={config.bcForgotPasswordUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.link}
                >
                  {t("general.forgot_password_link.text")}
                </a>
              </Grid>
            )}
            <Grid item className={classes.mobileWidth}>
              <LoginButton onClick={login} disabled={isProcessing} id="loginButton" className={classes.mobileWidth}>
                {t("general.login_button.text")}
              </LoginButton>
            </Grid>
          </Grid>
        </Box>
      </Grid>
    </Grid>
  );
};
